import BaseBehavior from '../../../framework/base/base';
import { debounce } from '../../../shared/utilities/debounce';
import { whenInView } from '../../../shared/utilities/when-in-view';
import { addEventListenerOnce } from '../../../shared/utilities/addListenerOnce';

const fixedTableClasses = {
	enabled: 'v2-fixed-table--enabled'
};

let isRtl = false;

export default class FixedTable extends BaseBehavior {
	constructor(element) {
		super(element);

		this.initDomCache();

		this.dom.table = element.querySelector('table');
		this.dom.fixedCells = element.querySelectorAll('.v2-table__fixed-col');
		this.isFixedTableInitialised = false;
		this.isVertical =
			element.hasAttribute('data-table-scroll-direction') &&
			element.getAttribute('data-table-scroll-direction').toLowerCase() === 'vertical';

		if (element.hasAttribute('data-table-height')) {
			element.style.height = `${parseInt(element.getAttribute('data-table-height'), 10)}px`;
			element.style.maxHeight = `${parseInt(element.getAttribute('data-table-height'), 10)}px`;
		}

		isRtl = window.getComputedStyle(element).direction === 'rtl';

		if (this.isHorizontallyScollable()) {
			this.setWidthAndHeight();
		}
		this.addEventListeners();
	}

	isHorizontallyScollable() {
		return this.element.clientWidth < this.dom.table.clientWidth;
	}

	resetColsHeight() {
		this.dom.fixedCells.forEach(element => {
			element.parentElement.style.height = 'auto';
			element.style.height = 'auto';
		});
	}

	resetColsWidth() {
		this.dom.fixedCells.forEach(element => {
			element.style.width = 'auto';
		});
		isRtl ? (this.element.style.marginRight = 'auto') : (this.element.style.marginLeft = 'auto');
	}

	resetColsWidthAndHeight() {
		this.resetColsHeight();
		this.resetColsWidth();
	}

	setWidthAndHeight() {
		if (this.isHorizontallyScollable() && this.isFixedTableInitialised) {
			this.resetColsWidthAndHeight();
			this.setColWidth();
			this.setColHeight();
			return;
		}
		if (this.isHorizontallyScollable()) {
			this.initFixedTable();
			return;
		}
		if (this.isFixedTableInitialised) this.resetColsWidthAndHeight();
		this.disableFixedTable();
	}

	initFixedTable() {
		return whenInView(this.dom.table).then(() => {
			this.dom.table.classList.add(fixedTableClasses.enabled);
			this.isFixedTableInitialised = true;
			this.setColWidth();
			this.setColHeight();
		});
	}

	disableFixedTable() {
		this.dom.table.classList.remove(fixedTableClasses.enabled);
		this.isFixedTableInitialised = false;
	}

	setColHeight() {
		this.dom.fixedCells.forEach(element => {
			const elHeight = element.getBoundingClientRect().height;
			const parentHeight = element.parentElement.getBoundingClientRect().height;

			elHeight >= parentHeight
				? (element.parentElement.style.height = elHeight + 'px')
				: (element.style.height = parentHeight + 'px');
		});
	}

	setColWidth() {
		let biggestWidth = 0;

		this.dom.fixedCells.forEach(element => {
			const colWidth = element.getBoundingClientRect().width;
			if (biggestWidth < colWidth) {
				biggestWidth = colWidth;
			}
		});
		this.dom.fixedCells.forEach(element => {
			element.style.width = biggestWidth + 'px';
		});
		isRtl
			? (this.element.style.marginRight = biggestWidth + 'px')
			: (this.element.style.marginLeft = biggestWidth + 'px');
	}

	addEventListeners() {
		if (this.isVertical) {
			this.addEventListener(this.element, 'scroll', () => {
				this.handleVerticalScroll();
			});
		}
		this.lastRecordedWindowWidth = window.innerWidth;
		const debouncedResizeHandler = debounce(() => {
			if (this.lastRecordedWindowWidth === window.innerWidth) return;
			this.lastRecordedWindowWidth = window.innerWidth;
			this.setWidthAndHeight();
		}, 250).bind(this);
		window.addEventListener('resize', debouncedResizeHandler);

		const debouncedImageLoadHandler = debounce(this.setWidthAndHeight.bind(this));
		Array.from(this.dom.table.querySelectorAll('img')).forEach(image => {
			addEventListenerOnce(image, 'load', debouncedImageLoadHandler);
		});
	}

	removeEventListeners() {
		this.removeEventListener(this.element, 'scroll');
		this.removeEventListener(window, 'resize');
	}

	handleVerticalScroll() {
		const offset = this.element.scrollTop;

		this.setValueOnCells('top', `${offset}px`);
	}

	setValueOnCells(property, value) {
		for (let i = 0; i < this.dom.fixedCells.length; i++) {
			this.dom.fixedCells[i].style[property] = value;
		}
	}
}
